// Dashboard Portal
// pages/Terms/TermsAcceptanceStep.js

import React, { useState, useEffect, useRef } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Button,
  Alert,
  CircularProgress,
  Divider,
  useTheme,
  LinearProgress,
  Tabs,
  Tab,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const colors = {
  textPrimary: '#1e293b',
  textSecondary: '#475569',
  textTertiary: '#94a3b8',
};

// Platform terms URLs
const PLATFORM_TERMS = [
  {
    id: 'terms-of-use',
    title: 'Terms of Use',
    url: 'https://klonit.ai/terms-of-use',
    description: 'General terms and conditions for using KlonIT services',
  },
  {
    id: 'privacy-policy',
    title: 'Privacy Policy',
    url: 'https://klonit.ai/privacy-policy',
    description: 'How we collect, use, and protect your data',
  },
];

export default function TermsAcceptanceStep({
  isAdmin = false,
  onComplete,
  onError,
  isSetupMode = true
}) {
  const theme = useTheme();
  const iframeRef = useRef(null);
  const viewTimerRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [terms, setTerms] = useState([]);
  const [error, setError] = useState(null);

  // Tab and viewing states
  const [activeTab, setActiveTab] = useState(0);
  const [viewedTerms, setViewedTerms] = useState({});
  const [viewProgress, setViewProgress] = useState({});
  const [acknowledged, setAcknowledged] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);

  // Minimum viewing time per term (in seconds)
  const MIN_VIEW_TIME = 5;

  useEffect(() => {
    fetchTerms();
  }, [isAdmin]);

  // Track viewing time for each term
  useEffect(() => {
    if (terms.length === 0 || loading) return;

    const currentTermId = terms[activeTab]?.id;
    if (!currentTermId) return;

    // Start progress timer
    const startTime = Date.now();
    const currentProgress = viewProgress[currentTermId] || 0;

    viewTimerRef.current = setInterval(() => {
      const elapsed = (Date.now() - startTime) / 1000;
      const totalTime = currentProgress + elapsed;
      const progress = Math.min((totalTime / MIN_VIEW_TIME) * 100, 100);

      setViewProgress(prev => ({
        ...prev,
        [currentTermId]: totalTime
      }));

      if (progress >= 100 && !viewedTerms[currentTermId]) {
        setViewedTerms(prev => ({
          ...prev,
          [currentTermId]: true
        }));
      }
    }, 500);

    return () => {
      if (viewTimerRef.current) {
        clearInterval(viewTimerRef.current);
      }
    };
  }, [activeTab, terms, loading]);

  const fetchTerms = async () => {
    try {
      setLoading(true);
      setError(null);

      if (isAdmin) {
        // Use platform terms for admin
        setTerms(PLATFORM_TERMS);
        const initialViewed = {};
        const initialProgress = {};
        PLATFORM_TERMS.forEach(term => {
          initialViewed[term.id] = false;
          initialProgress[term.id] = 0;
        });
        setViewedTerms(initialViewed);
        setViewProgress(initialProgress);
      } else {
        await fetchUserTerms();
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || "Failed to load terms";
      setError(errorMsg);
      if (onError) onError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserTerms = async () => {
    const response = await axiosInstance.get('/api/terms/pending');

    if (response.data.success) {
      const pendingTerms = response.data.pending_terms || [];

      // If no pending terms from API, use platform terms
      if (pendingTerms.length === 0) {
        setTerms(PLATFORM_TERMS);
        const initialViewed = {};
        const initialProgress = {};
        PLATFORM_TERMS.forEach(term => {
          initialViewed[term.id] = false;
          initialProgress[term.id] = 0;
        });
        setViewedTerms(initialViewed);
        setViewProgress(initialProgress);
      } else {
        setTerms(pendingTerms);
        const initialViewed = {};
        const initialProgress = {};
        pendingTerms.forEach(term => {
          initialViewed[term.id] = false;
          initialProgress[term.id] = 0;
        });
        setViewedTerms(initialViewed);
        setViewProgress(initialProgress);
      }
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setIframeLoaded(false);
  };

  const handleIframeLoad = () => {
    setIframeLoaded(true);
  };

  const allTermsViewed = () => {
    return terms.length > 0 && terms.every(term => viewedTerms[term.id] === true);
  };

  const getOverallProgress = () => {
    if (terms.length === 0) return 0;
    const viewedCount = terms.filter(term => viewedTerms[term.id]).length;
    return Math.round((viewedCount / terms.length) * 100);
  };

  const getCurrentTermProgress = () => {
    const currentTermId = terms[activeTab]?.id;
    if (!currentTermId) return 0;
    const time = viewProgress[currentTermId] || 0;
    return Math.min((time / MIN_VIEW_TIME) * 100, 100);
  };

  const updateUserTermToLocalStorage = () => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const userData = JSON.parse(userStr);
      userData.active_team.has_pending_terms = false;
      userData.active_team.pending_terms_count = 0;
      localStorage.setItem('user', JSON.stringify(userData));
      window.dispatchEvent(new CustomEvent('userLocalStorageUpdated', { detail: userData }));
    }
  };

  const handleAcceptTerms = async () => {
    if (!acknowledged) {
      setError("Please acknowledge that you have read and understood all terms");
      return;
    }

    if (!allTermsViewed()) {
      setError("Please view all terms before accepting");
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const termIds = terms.map((term) => term.id);

      if (!isAdmin) {
        await axiosInstance.post('/api/terms/accept', {
          term_ids: termIds,
        });
      }

      updateUserTermToLocalStorage();
      if (onComplete) {
        const nextStep = isAdmin ? '/select-plan' : '/chatbots';
        onComplete(nextStep);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || "Failed to accept terms";
      setError(errorMsg);
      if (onError) onError(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const openInNewTab = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 5 }}>
        <CircularProgress size={36} sx={{ color: theme.palette.primary.main }} />
        <Typography sx={{ mt: 2, fontFamily, color: colors.textSecondary, fontSize: '0.875rem' }}>
          Loading terms...
        </Typography>
      </Box>
    );
  }

  if (terms.length === 0) {
    return (
      <Alert severity="info" sx={{ fontFamily, borderRadius: '14px' }}>
        No terms to accept. You can continue to the next step.
      </Alert>
    );
  }

  const currentTerm = terms[activeTab];

  return (
    <Box sx={{ fontFamily }}>
      {/* Header */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.5, mb: 2,
        p: 2, borderRadius: '14px',
        bgcolor: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.15)',
      }}>
        <Box sx={{
          width: 36, height: 36, borderRadius: '10px',
          background: 'linear-gradient(135deg, #f59e0b, #d97706)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <GavelOutlinedIcon sx={{ color: '#fff', fontSize: '1.1rem' }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.875rem', color: colors.textPrimary }}>
            Terms Acceptance Required
          </Typography>
          <Typography sx={{ fontFamily, fontSize: '0.8rem', color: colors.textSecondary }}>
            Please review all {terms.length} document{terms.length !== 1 ? 's' : ''} below and acknowledge to continue.
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography sx={{ fontFamily, fontSize: '0.75rem', fontWeight: 600, color: colors.textPrimary }}>
            {getOverallProgress()}% Complete
          </Typography>
          <Typography sx={{ fontFamily, fontSize: '0.7rem', color: colors.textTertiary }}>
            {terms.filter(t => viewedTerms[t.id]).length} of {terms.length} viewed
          </Typography>
        </Box>
      </Box>

      {/* Overall Progress Bar */}
      <LinearProgress
        variant="determinate"
        value={getOverallProgress()}
        sx={{
          mb: 2, height: 6, borderRadius: 3,
          bgcolor: 'rgba(0,0,0,0.06)',
          '& .MuiLinearProgress-bar': {
            borderRadius: 3,
            background: allTermsViewed()
              ? 'linear-gradient(90deg, #059669, #10b981)'
              : `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
          },
        }}
      />

      {/* Terms Tabs */}
      <Box sx={{
        bgcolor: 'rgba(255,255,255,0.7)',
        borderRadius: '14px 14px 0 0',
        border: '1px solid rgba(0,0,0,0.06)',
        borderBottom: 'none',
      }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 48,
            '& .MuiTabs-indicator': {
              bgcolor: theme.palette.primary.main,
              height: 3,
              borderRadius: '3px 3px 0 0',
            },
            '& .MuiTab-root': {
              fontFamily,
              fontSize: '0.8rem',
              fontWeight: 500,
              textTransform: 'none',
              minHeight: 48,
              color: colors.textSecondary,
              '&.Mui-selected': {
                color: theme.palette.primary.main,
                fontWeight: 600,
              },
            },
          }}
        >
          {terms.map((term, index) => (
            <Tab
              key={term.id}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  {viewedTerms[term.id] ? (
                    <CheckCircleOutlineIcon sx={{ fontSize: '1rem', color: '#059669' }} />
                  ) : (
                    <DescriptionOutlinedIcon sx={{ fontSize: '1rem' }} />
                  )}
                  {term.title}
                </Box>
              }
            />
          ))}
        </Tabs>
      </Box>

      {/* Current Term Info Bar */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        p: 1.5, bgcolor: 'rgba(248,250,252,0.9)',
        borderLeft: '1px solid rgba(0,0,0,0.06)',
        borderRight: '1px solid rgba(0,0,0,0.06)',
      }}>
        <Box>
          <Typography sx={{ fontFamily, fontSize: '0.8rem', fontWeight: 600, color: colors.textPrimary }}>
            {currentTerm?.title}
          </Typography>
          {currentTerm?.description && (
            <Typography sx={{ fontFamily, fontSize: '0.7rem', color: colors.textTertiary }}>
              {currentTerm.description}
            </Typography>
          )}
        </Box>
        <Button
          size="small"
          onClick={() => openInNewTab(currentTerm?.url || currentTerm?.file_url)}
          endIcon={<OpenInNewIcon sx={{ fontSize: '0.9rem' }} />}
          sx={{
            fontFamily, fontSize: '0.75rem', textTransform: 'none',
            color: theme.palette.primary.main,
          }}
        >
          Open in New Tab
        </Button>
      </Box>

      {/* Iframe Viewer */}
      <Box sx={{
        position: 'relative',
        height: '380px',
        borderRadius: '0 0 14px 14px',
        overflow: 'hidden',
        border: '1px solid rgba(0,0,0,0.06)',
        borderTop: 'none',
        bgcolor: '#fff',
      }}>
        {/* Loading overlay */}
        {!iframeLoaded && (
          <Box sx={{
            position: 'absolute', inset: 0, zIndex: 10,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            bgcolor: 'rgba(255,255,255,0.95)',
          }}>
            <CircularProgress size={32} sx={{ color: theme.palette.primary.main, mb: 1.5 }} />
            <Typography sx={{ fontFamily, fontSize: '0.85rem', color: colors.textSecondary }}>
              Loading {currentTerm?.title}...
            </Typography>
          </Box>
        )}

        <iframe
          ref={iframeRef}
          src={currentTerm?.url || currentTerm?.file_url}
          title={currentTerm?.title}
          onLoad={handleIframeLoad}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
          }}
        />

        {/* View progress indicator */}
        {!viewedTerms[currentTerm?.id] && (
          <Box sx={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            p: 1, bgcolor: 'rgba(59,130,246,0.95)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
          }}>
            <CircularProgress
              size={16}
              variant="determinate"
              value={getCurrentTermProgress()}
              sx={{ color: '#fff' }}
            />
            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#fff', fontWeight: 500 }}>
              {getCurrentTermProgress() < 100
                ? `Viewing... ${Math.round(getCurrentTermProgress())}%`
                : 'Document reviewed!'}
            </Typography>
          </Box>
        )}
      </Box>

      <Divider sx={{ my: 2, borderColor: 'rgba(0,0,0,0.06)' }} />

      {/* Acknowledgment Checkbox */}
      <Box sx={{
        p: 2, borderRadius: '14px', mb: 2,
        bgcolor: acknowledged ? 'rgba(5,150,105,0.04)' : 'rgba(255,255,255,0.7)',
        border: acknowledged ? '1px solid rgba(5,150,105,0.2)' : '1px solid rgba(0,0,0,0.06)',
        opacity: allTermsViewed() ? 1 : 0.6,
        transition: 'all 0.3s ease',
      }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              disabled={!allTermsViewed() || submitting}
              size="small"
              sx={{ '&.Mui-checked': { color: '#059669' } }}
            />
          }
          label={
            <Box>
              <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: colors.textPrimary }}>
                I acknowledge that I have read and understood all terms
              </Typography>
              <Typography sx={{ fontFamily, fontSize: '0.75rem', color: colors.textSecondary, mt: 0.25 }}>
                {!allTermsViewed()
                  ? `Please view all ${terms.length} documents to enable this option (${terms.filter(t => viewedTerms[t.id]).length} of ${terms.length} viewed)`
                  : "By checking this box, you confirm you have reviewed all terms and conditions"}
              </Typography>
            </Box>
          }
        />
      </Box>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2, fontFamily, borderRadius: '14px' }}>
          {error}
        </Alert>
      )}

      {/* Important Notice */}
      <Box sx={{
        display: 'flex', alignItems: 'flex-start', gap: 1.5,
        p: 2, mb: 2, borderRadius: '14px',
        bgcolor: 'rgba(59,130,246,0.04)', border: '1px solid rgba(59,130,246,0.1)',
      }}>
        <InfoOutlinedIcon sx={{ color: theme.palette.primary.main, fontSize: '1.1rem', mt: 0.25 }} />
        <Box>
          <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.8rem', color: colors.textPrimary, mb: 0.25 }}>
            Important Notice
          </Typography>
          <Typography sx={{ fontFamily, fontSize: '0.75rem', color: colors.textSecondary }}>
            By accepting these terms, you acknowledge that you have read, understood,
            and agree to be bound by the conditions outlined in each document.
            {isAdmin && " These terms govern your use of the KlonIT platform."}
          </Typography>
        </Box>
      </Box>

      {/* Action Area */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          {allTermsViewed() ? (
            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#059669', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CheckCircleOutlineIcon sx={{ fontSize: '0.9rem' }} />
              All {terms.length} documents reviewed
            </Typography>
          ) : (
            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: colors.textTertiary }}>
              {terms.filter(t => viewedTerms[t.id]).length} of {terms.length} documents reviewed
            </Typography>
          )}
        </Box>

        <Button
          onClick={handleAcceptTerms}
          disabled={!acknowledged || !allTermsViewed() || submitting}
          endIcon={submitting
            ? <CircularProgress size={16} color="inherit" />
            : <CheckCircleOutlineIcon sx={{ fontSize: '1rem' }} />
          }
          sx={{
            fontFamily, textTransform: 'none', fontWeight: 600, fontSize: '0.875rem',
            color: '#fff', borderRadius: '14px', px: 4, py: 1.25,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            boxShadow: `0 4px 16px ${theme.palette.primary.main}40`,
            '&:hover': {
              background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
              boxShadow: `0 6px 20px ${theme.palette.primary.main}50`,
              transform: 'translateY(-1px)',
            },
            '&:disabled': { opacity: 0.5, background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`, color: '#fff' },
            transition: 'all 0.2s ease',
          }}
        >
          {submitting ? "Accepting..." : "Accept Terms & Continue"}
        </Button>
      </Box>
    </Box>
  );
}
